import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Captions,
  ChevronRight,
  CircleDollarSign,
  Clapperboard,
  GitBranch,
  Layers3,
  LoaderCircle,
  Mic2,
  PackageOpen,
  PlayCircle,
  ShieldCheck,
  BarChart3,
  FileUp,
  Target,
  Video,
} from "lucide-react";
import { api } from "../api";
import type {
  AvatarRefreshReport,
  CreativeAgentManifest,
  DemoMedia,
  LiveDemoResults,
  ProductSummary,
  VersionControlSnapshot,
  ContentQualityReport,
  PerformanceMetric,
  SellingPoints,
} from "../types";
import type { MultiShotDemo } from "../types";
import { StatusBadge } from "../components/StatusBadge";
import { ContentVersionEditor } from "../components/ContentVersionEditor";
import { GenerationRoutePanel } from "../components/GenerationRoutePanel";
import { VideoVersionHistory } from "../components/VideoVersionHistory";
import { ProductImageManager } from "../components/ProductImageManager";

type MaterialTab =
  | "demo"
  | "selling"
  | "scripts"
  | "storyboard"
  | "quality"
  | "routes"
  | "tasks";

interface MaterialsPageProps {
  products: ProductSummary[];
  searchValue: string;
  initialProductId: string | null;
}

export function MaterialsPage({
  products,
  searchValue,
  initialProductId,
}: MaterialsPageProps) {
  const filteredProducts = useMemo(() => {
    const query = searchValue.trim().toLowerCase();
    if (!query) return products;
    return products.filter((product) =>
      `${product.product_id} ${product.product_name} ${product.category}`
        .toLowerCase()
        .includes(query),
    );
  }, [products, searchValue]);

  const [selectedProductId, setSelectedProductId] = useState(
    initialProductId ?? products[0]?.product_id ?? "",
  );
  const [activeTab, setActiveTab] = useState<MaterialTab>("demo");
  const [scriptIndex, setScriptIndex] = useState(0);

  const detailQuery = useQuery({
    queryKey: ["product", selectedProductId],
    queryFn: () => api.getProduct(selectedProductId),
    enabled: Boolean(selectedProductId),
    refetchInterval: (query) =>
      query.state.data?.generation_requests.some((request) =>
        ["queued", "leased", "running", "waiting_provider", "downloading", "composing"].includes(request.status),
      )
        ? 3_000
        : false,
  });

  const detail = detailQuery.data;
  const productImages = detail?.product_images ?? {
    schema_version: 1,
    product_id: selectedProductId,
    selected_asset_id: "",
    updated_at: "",
    assets: [],
  };
  const activeScript = detail?.scripts[scriptIndex] ?? detail?.scripts[0];
  const activePrompt = detail?.prompts[scriptIndex] ?? detail?.prompts[0];
  const tabs: Array<{ id: MaterialTab; label: string; icon: typeof Target }> = [
    { id: "demo", label: "真实样例", icon: PlayCircle },
    { id: "selling", label: "卖点拆解", icon: Target },
    { id: "scripts", label: "脚本版本", icon: Captions },
    { id: "storyboard", label: "分镜表", icon: Clapperboard },
    { id: "quality", label: "质量与数据", icon: BarChart3 },
    { id: "routes", label: "生成路由", icon: GitBranch },
    { id: "tasks", label: "媒体任务", icon: Video },
  ];

  return (
    <div className="materials-layout">
      <aside className="product-browser">
        <div className="browser-heading">
          <span>{filteredProducts.length} ITEMS</span>
          <strong>商品素材包</strong>
        </div>
        <div className="product-browser-list">
          {filteredProducts.map((product) => (
            <button
              key={product.product_id}
              type="button"
              className={
                selectedProductId === product.product_id
                  ? "browser-product is-active"
                  : "browser-product"
              }
              onClick={() => {
                setSelectedProductId(product.product_id);
                setScriptIndex(0);
              }}
            >
              <span className="product-code">{product.product_id}</span>
              <div>
                <strong>{product.product_name}</strong>
                <span>{product.category}</span>
              </div>
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          ))}
        </div>
      </aside>

      <section className="material-stage">
        {detailQuery.isLoading && (
          <div className="state-panel">
            <LoaderCircle className="is-spinning" size={24} aria-hidden="true" />
            <strong>正在读取素材包</strong>
          </div>
        )}
        {detailQuery.isError && (
          <div className="state-panel is-error">
            <PackageOpen size={24} aria-hidden="true" />
            <strong>{detailQuery.error.message}</strong>
          </div>
        )}
        {detail && (
          <>
            <div className="material-hero">
              <div className="material-title">
                <span className="eyebrow">{detail.product.product_id} / PACKAGE</span>
                <h2>{detail.product.product_name}</h2>
                <p>
                  {detail.product.target_user} · {detail.product.platform} ·{" "}
                  {detail.product.video_duration} 秒
                </p>
              </div>
              <div className="material-facts">
                <div>
                  <span>定价</span>
                  <strong>{detail.product.price}</strong>
                </div>
                <div>
                  <span>脚本</span>
                  <strong>{detail.scripts.length} 版</strong>
                </div>
                <div>
                  <span>文件</span>
                  <strong>{detail.files.length} 个</strong>
                </div>
              </div>
            </div>

            <ProductImageManager
              productId={detail.product.product_id}
              registry={productImages}
              onUpdated={() => detailQuery.refetch()}
            />

            <nav className="material-tabs" aria-label="素材详情导航">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    type="button"
                    key={tab.id}
                    className={activeTab === tab.id ? "is-active" : ""}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <Icon size={16} aria-hidden="true" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>

            <div className="material-content">
              {activeTab === "demo" && (
                <DemoShowcase
                  media={detail.demo_media}
                  liveDemo={detail.live_demo}
                  avatarRefresh={detail.avatar_refresh}
                  multishotDemo={detail.multishot_demo}
                  creativeAgent={detail.creative_agent}
                  productId={detail.product.product_id}
                  versionControl={detail.version_control}
                  onVersionUpdated={() => detailQuery.refetch()}
                />
              )}

              {activeTab === "selling" && (
                <SellingPointGrid
                  points={detail.selling_points}
                  brandTone={detail.product.brand_tone}
                />
              )}
              {activeTab === "quality" && (
                <QualityPerformancePanel reports={detail.content_quality.reports} metrics={detail.performance_metrics} onUpdated={() => detailQuery.refetch()} />
              )}

              {(activeTab === "scripts" ||
                activeTab === "storyboard" ||
                activeTab === "routes" ||
                activeTab === "tasks") && (
                <div className="version-switcher" role="tablist" aria-label="脚本版本">
                  {detail.scripts.map((script, index) => (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={scriptIndex === index}
                      key={script.version_id}
                      className={scriptIndex === index ? "is-active" : ""}
                      onClick={() => {
                        setScriptIndex(index);
                      }}
                    >
                      <span>V{index + 1}</span>
                      {script.version_name} · {script.hook_type || "旧版钩子"}
                    </button>
                  ))}
                </div>
              )}

              {(activeTab === "scripts" || activeTab === "storyboard") &&
                activeScript &&
                detail.version_control.scripts[activeScript.version_id] && (
                  <ContentVersionEditor
                    key={`${detail.product.product_id}-${activeScript.version_id}-${detail.version_control.scripts[activeScript.version_id].current_revision_id}`}
                    productId={detail.product.product_id}
                    mode={
                      activeTab === "scripts" ? "script" : "storyboard"
                    }
                    script={activeScript}
                    track={
                      detail.version_control.scripts[
                        activeScript.version_id
                      ]
                    }
                    onSaved={() => detailQuery.refetch()}
                    onUseForGeneration={() => setActiveTab("routes")}
                  />
                )}

              {activeTab === "routes" &&
                activeScript &&
                detail.version_control.scripts[activeScript.version_id] && (
                <GenerationRoutePanel
                  key={`${activeScript.version_id}-${detail.version_control.scripts[activeScript.version_id].current_revision_id}`}
                  productId={detail.product.product_id}
                  script={activeScript}
                  track={
                    detail.version_control.scripts[activeScript.version_id]
                  }
                  requests={detail.generation_requests}
                  onUpdated={() => detailQuery.refetch()}
                />
              )}

              {activeTab === "tasks" && activePrompt && (
                <div className="task-grid">
                  {Object.entries(activePrompt.tasks).map(([taskType, task]) => {
                    const Icon =
                      taskType === "video"
                        ? Video
                        : taskType === "avatar"
                          ? Layers3
                          : Mic2;
                    return (
                      <article className="task-card" key={taskType}>
                        <div className="task-icon">
                          <Icon size={19} aria-hidden="true" />
                        </div>
                        <div className="task-card-heading">
                          <span>{taskType.toUpperCase()}</span>
                          <StatusBadge compact status={task.status} />
                        </div>
                        <code>{task.task_id}</code>
                        <dl>
                          <div>
                            <dt>Provider</dt>
                            <dd>{task.provider}</dd>
                          </div>
                          <div>
                            <dt>Cost</dt>
                            <dd>
                              <CircleDollarSign size={13} aria-hidden="true" />
                              {task.estimated_cost.toFixed(2)}
                            </dd>
                          </div>
                        </dl>
                      </article>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}

function DemoShowcase({
  media,
  liveDemo,
  avatarRefresh,
  multishotDemo,
  creativeAgent,
  productId,
  versionControl,
  onVersionUpdated,
}: {
  media: DemoMedia[];
  liveDemo: LiveDemoResults | null;
  avatarRefresh: AvatarRefreshReport | null;
  multishotDemo: MultiShotDemo | null;
  creativeAgent: CreativeAgentManifest | null;
  productId: string;
  versionControl: VersionControlSnapshot;
  onVersionUpdated: () => Promise<unknown>;
}) {
  if (!liveDemo && !creativeAgent && media.length === 0) {
    return (
      <div className="state-panel">
        <PlayCircle size={24} aria-hidden="true" />
        <strong>当前商品还没有真实调用演示</strong>
      </div>
    );
  }

  const serviceItems = liveDemo
    ? [
        ["OpenAI", liveDemo.services.llm.provider, liveDemo.services.llm.status],
        ["豆包 TTS", liveDemo.services.tts.provider, liveDemo.services.tts.status],
        ["火山 TOS", liveDemo.services.storage.provider, liveDemo.services.storage.status],
        ["OmniHuman", liveDemo.services.avatar.provider, liveDemo.services.avatar.status],
        ["Seedance", liveDemo.services.video.provider, liveDemo.services.video.status],
      ]
    : [];
  const finalMediaName = multishotDemo?.output_file.split("/").at(-1) ?? "";
  const finalMedia = media.find((item) => item.name === finalMediaName);
  const hasAvatarV2 = media.some((item) =>
    item.name.includes("avatar-pain_seed-clear-male-v2"),
  );
  const hasTtsV2 = media.some((item) =>
    item.name.includes("tts-pain_seed-clear-male-v2"),
  );
  const supportingMedia = media.filter(
    (item) =>
      item.name !== finalMediaName &&
      !(hasAvatarV2 && item.name === "avatar-pain_seed.mp4") &&
      !(hasTtsV2 && item.name === "tts-pain_seed.mp3"),
  );

  return (
    <div className="demo-showcase">
      <section className="demo-summary">
        <div>
          <span className="document-label">
            {liveDemo ? "LIVE SERVICE DEMO" : "XIAOYUNQUE / FAST TRACK"}
          </span>
          <h3>
            {liveDemo ? "全链路真实调用已完成" : "小云雀真实成片已归档"}
          </h3>
          {liveDemo ? (
            <p>
              Run ID：<code>{liveDemo.run_id}</code> · 仅痛点种草版运行真实媒体节点
            </p>
          ) : (
            <p>
              Task ID：<code>{creativeAgent?.task_id ?? "未记录"}</code> ·{" "}
              {creativeAgent?.script_version_name ?? "已审核脚本"}
            </p>
          )}
        </div>
        <div className="demo-security">
          <ShieldCheck size={20} aria-hidden="true" />
          <span>
            {liveDemo
              ? "凭证与临时签名 URL 未导出"
              : "本地成片已保存，发布前仍需人工终审"}
          </span>
        </div>
      </section>

      {liveDemo && (
        <div className="demo-services">
          {serviceItems.map(([label, provider, status]) => (
            <article key={label}>
              <span>{label}</span>
              <strong>{provider}</strong>
              <StatusBadge compact status={status} />
            </article>
          ))}
        </div>
      )}

      {creativeAgent && (
        <section className="demo-script-note">
          <span>
            小云雀 Agent 1.0 · {creativeAgent.script_version_name}
          </span>
          <p>
            任务 {creativeAgent.task_id} · 状态 {creativeAgent.status} ·{" "}
            {creativeAgent.is_mock ? "安全预览" : "真实付费调用"}
          </p>
          <small>
            预估费用 ¥{creativeAgent.estimated_cost.toFixed(2)} ·{" "}
            {creativeAgent.human_review_required
              ? "发布前需要最终人工审核"
              : "已完成自动审核"}
          </small>
        </section>
      )}

      {avatarRefresh && (
        <section className="demo-script-note">
          <span>数字人口播 v2 · {avatarRefresh.voice.label}</span>
          <p>{avatarRefresh.voice.script}</p>
          <small>
            音频 {avatarRefresh.voice.duration_seconds.toFixed(2)}s · 视频{" "}
            {avatarRefresh.avatar.duration_seconds.toFixed(2)}s · 语速参数{" "}
            {avatarRefresh.voice.speech_rate} · 720P 快速模式
          </small>
        </section>
      )}

      {multishotDemo && (
        <MultiShotShowcase
          demo={multishotDemo}
          finalMedia={finalMedia}
        />
      )}

      <VideoVersionHistory
        key={`${productId}-${versionControl.selected_final_video_version_id}`}
        productId={productId}
        versionControl={versionControl}
        onUpdated={onVersionUpdated}
      />

      <div className="demo-media-grid">
        {supportingMedia.map((item) => (
          <article className="demo-media-card" key={item.name}>
            <div className="demo-media-heading">
              <div>
                <div className="demo-media-tags">
                  <span className={`demo-provider-tag is-${item.provider}`}>
                    {item.provider_label}
                  </span>
                  <span className="demo-version-tag">{item.badge}</span>
                </div>
                <strong>{item.title}</strong>
                <code title={item.name}>{item.name}</code>
              </div>
              <small>
                {item.kind === "video" ? "VIDEO" : "AUDIO"} ·{" "}
                {formatBytes(item.bytes)}
              </small>
            </div>
            {item.kind === "video" ? (
              <video controls preload="metadata" src={item.url}>
                当前浏览器不支持视频播放。
              </video>
            ) : (
              <audio controls preload="metadata" src={item.url}>
                当前浏览器不支持音频播放。
              </audio>
            )}
          </article>
        ))}
      </div>

      {liveDemo && (
        <section className="demo-script-note">
          <span>媒体短版口播</span>
          <p>{liveDemo.media_script.voiceover}</p>
          <small>{liveDemo.cost_note}</small>
        </section>
      )}
    </div>
  );
}

function MultiShotShowcase({
  demo,
  finalMedia,
}: {
  demo: MultiShotDemo;
  finalMedia: DemoMedia | undefined;
}) {
  const isPreview = demo.mode === "existing-assets-preview";
  const [playbackTime, setPlaybackTime] = useState(0);
  const activeShot =
    demo.shots.find(
      (shot) =>
        playbackTime >= shot.timeline_start &&
        playbackTime < shot.timeline_end,
    ) ?? demo.shots.at(-1);

  return (
    <section className="multishot-showcase">
      <div className="multishot-heading">
        <div className="multishot-title">
          <span className="document-label">AUTO EDIT / 7 SHOTS</span>
          <h3>多镜头自动剪辑成片</h3>
          <p>
            {isPreview
              ? "现有真实素材复用预览，不新增 Seedance 付费调用"
              : "7 个独立 Seedance 镜头已进入正式成片"}
          </p>
        </div>
        <StatusBadge compact status={demo.status} />
      </div>

      <div className="multishot-body">
        <div className="multishot-player">
          {finalMedia ? (
            <div className="multishot-video-frame">
              <video
                controls
                preload="metadata"
                src={finalMedia.url}
                onLoadedMetadata={(event) =>
                  setPlaybackTime(event.currentTarget.currentTime)
                }
                onTimeUpdate={(event) =>
                  setPlaybackTime(event.currentTarget.currentTime)
                }
                onSeeked={(event) =>
                  setPlaybackTime(event.currentTarget.currentTime)
                }
              >
                当前浏览器不支持视频播放。
              </video>
              {activeShot ? (
                <div className="multishot-caption" aria-live="polite">
                  {activeShot.subtitle}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="state-panel is-error">
              <Video size={22} aria-hidden="true" />
              <strong>成片文件尚未找到</strong>
            </div>
          )}
        </div>
        <aside className="multishot-meta">
          <dl>
            <div>
              <dt>镜头进度</dt>
              <dd>
                {demo.completed_shot_count}/{demo.shot_count}
              </dd>
            </div>
            <div>
              <dt>成片时长</dt>
              <dd>{demo.total_duration.toFixed(0)}s</dd>
            </div>
            <div>
              <dt>画幅</dt>
              <dd>
                {demo.composition
                  ? `${demo.composition.width}×${demo.composition.height}`
                  : "9:16"}
              </dd>
            </div>
            <div>
              <dt>新增付费任务</dt>
              <dd>{demo.paid_calls_submitted ? "是" : "否"}</dd>
            </div>
          </dl>
          <p>{demo.source_note}</p>
        </aside>
      </div>

      <div className="multishot-timeline" aria-label="多镜头任务时间轴">
        {demo.shots.map((shot) => (
          <article key={shot.shot_no}>
            <div>
              <span>SHOT {String(shot.shot_no).padStart(2, "0")}</span>
              <StatusBadge compact status={shot.task.status} />
            </div>
            <strong>{shot.target_duration.toFixed(0)}s</strong>
            <small>
              {shot.asset_reused
                ? "复用素材"
                : `生成请求 ${shot.request_duration}s`}
            </small>
          </article>
        ))}
      </div>
    </section>
  );
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function SellingPointGrid({
  points,
  brandTone,
}: {
  points: SellingPoints;
  brandTone: string;
}) {
  const groups = [
    { title: "目标人群", items: points.target_audience, tone: "dark" },
    { title: "用户痛点", items: points.user_pain_points, tone: "orange" },
    { title: "核心卖点", items: points.core_selling_points, tone: "green" },
    { title: "使用场景", items: points.use_scenarios, tone: "paper" },
    { title: "信任背书", items: points.trust_backing, tone: "paper" },
    { title: "风险提醒", items: points.risk_warnings, tone: "warning" },
  ];
  return (
    <div className="selling-grid">
      <article className="tone-banner">
        <span>BRAND TONE</span>
        <strong>{brandTone}</strong>
        <p>生成模式：{points.generation_mode}</p>
      </article>
      {points.fabv_points?.map((point) => (
        <article className="fabv-evidence-card" key={point.selling_point_id}>
          <header><span>{point.selling_point_id}</span><strong>{point.feature}</strong></header>
          <dl>
            <div><dt>优势</dt><dd>{point.advantage}</dd></div>
            <div><dt>用户利益</dt><dd>{point.benefit}</dd></div>
            <div><dt>场景价值</dt><dd>{point.value}</dd></div>
            <div><dt>前后场景</dt><dd>{point.before_after_scene}</dd></div>
          </dl>
          <blockquote>{point.colloquial_expression}</blockquote>
          <small>模型口语化改写 · 依据 {point.evidence_refs.join("、") || "待补充"}</small>
        </article>
      ))}
      {groups.map((group) => (
        <article className={`selling-block tone-${group.tone}`} key={group.title}>
          <h3>{group.title}</h3>
          <ul>
            {group.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

function QualityPerformancePanel({ reports, metrics, onUpdated }: { reports: ContentQualityReport[]; metrics: PerformanceMetric[]; onUpdated: () => Promise<unknown>; }) {
  const [feedback, setFeedback] = useState("");
  const [busy, setBusy] = useState(false);
  const configQuery = useQuery({ queryKey: ["config"], queryFn: api.getConfig });
  const canImport = configQuery.data?.capabilities.importCsv ?? true;
  async function importCsv(file: File) {
    setBusy(true); setFeedback("");
    try {
      const result = await api.importPerformanceCsv(file.name, await file.text());
      setFeedback(`已导入 ${result.imported_count} 条，跳过重复 ${result.duplicate_count} 条`);
      await onUpdated();
    } catch (error) { setFeedback(error instanceof Error ? error.message : "导入失败"); }
    finally { setBusy(false); }
  }
  return (
    <div className="quality-performance-layout">
      <section className="quality-report-grid">
        {reports.map((report) => (
          <article className={report.hard_blocked ? "quality-card is-blocked" : "quality-card"} key={report.script_version_id}>
            <header><div><span>{report.script_version_id}</span><strong>{report.overall_score}</strong></div><StatusBadge compact status={report.hard_blocked ? "blocked" : "completed"} /></header>
            <p>内容制作质量评分，不代表完播率或转化率预测。</p>
            <ul>{report.issues.length === 0 ? <li>未发现自动检查问题</li> : report.issues.map((issue) => <li key={`${issue.code}-${issue.location}`}><code>{issue.code}</code><span>{issue.message}</span><small>{issue.suggestion}</small></li>)}</ul>
          </article>
        ))}
      </section>
      <section className="performance-ledger">
        <header><div><span className="document-label">PERFORMANCE LEDGER</span><strong>投放指标回流</strong></div><label className="performance-upload" aria-disabled={!canImport}><FileUp size={15} aria-hidden="true" />{canImport ? (busy ? "正在导入" : "导入 CSV") : "公开 Demo 禁止上传"}<input type="file" accept=".csv,text/csv" disabled={busy || !canImport} onChange={(event) => { const file = event.target.files?.[0]; if (file) void importCsv(file); event.currentTarget.value = ""; }} /></label></header>
        <p>比例使用 0-1；案例只有经人工批准后才可进入后续 Prompt。</p>
        {feedback && <div className="version-feedback">{feedback}</div>}
        <div className="performance-table-wrap"><table><thead><tr><th>版本</th><th>曝光</th><th>3秒留存</th><th>完播</th><th>点击</th><th>案例</th></tr></thead><tbody>
          {metrics.map((metric) => <tr key={metric.metric_id}><td><strong>{metric.script_version_id}</strong><small>{metric.video_version_id || metric.script_revision_id}</small></td><td>{metric.impressions.toLocaleString()}</td><td>{(metric.three_second_view_rate * 100).toFixed(1)}%</td><td>{(metric.completion_rate * 100).toFixed(1)}%</td><td>{(metric.click_through_rate * 100).toFixed(1)}%</td><td><button type="button" onClick={async () => { await api.setPerformanceExampleApproval(metric.metric_id, !metric.approved_example); await onUpdated(); }}>{metric.approved_example ? "撤销批准" : "批准案例"}</button></td></tr>)}
        </tbody></table></div>
      </section>
    </div>
  );
}
